import CalendarIllustration from "@/components/ui/CalendarIllustration.tsx";
import { useModalAction, useModalState } from "@/contexts/modal/Context.tsx";
import { useBookDiplomaRequestMutation } from "@/data/diploma.ts";
import { useMe } from "@/data/user.ts";
import { CalenderItem } from "@/types";
import Button from "@edusogno/primitives/button";
import LoadingButton from "@edusogno/primitives/loading-button";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

interface Props {
  isRequestedAlready: boolean;
  date: string;
  hour: string;
  calendar: CalenderItem;
}

export default function ConfirmDiplomaRequestModal() {
  const { data } = useModalState();
  const { isRequestedAlready, date, calendar, hour } = data as Props;
  const { closeModal } = useModalAction();
  const { me } = useMe();
  const { mutate, isLoading } = useBookDiplomaRequestMutation(closeModal);
  const { t } = useTranslation();

  const handleConfirm = () => {
    if (!me) return;

    mutate({
      start: dayjs(calendar.date).format("YYYY-MM-DD HH:mm"),
      summary: me.item.user.first_name + " " + me.item.user.last_name,
      calendar: [calendar.calendar],
      attendees: [me.item.user.email],
      newStatus: "Upselling scheduled",
      user_token: me.item.user.token,
      isUpselling: true,
      calendar_id: calendar.calendar_id,
    });
  };

  return (
    <div>
      <header className="flex flex-col items-center">
        {isRequestedAlready ? (
          <p className="text-brand text-2xl font-semibold">
            {t("diploma_request.modal.already_scheduled")}
          </p>
        ) : (
          <div className="space-y-1.5">
            <p className="text-brand text-2xl font-semibold">
              {t("diploma_request.modal.confirm_message")}
            </p>

            <p className="text-14px text-brand-secondary">
              {t("diploma_request.modal.calendar_invite")}&nbsp;
              <b className="text-brand">
                {t("diploma_request.modal.email_address")}
              </b>
              &nbsp;
              {t("diploma_request.modal.calendar_invite_end")}
            </p>
          </div>
        )}
      </header>

      <div className="flex flex-col justify-center h-full gap-y-10 mb-5">
        <div className=" flex flex-row items-center justify-center w-full gap-10">
          <CalendarIllustration
            day={dayjs(date).format("DD")}
            month={dayjs(date).format("MMM")}
            header={dayjs(date).format("ddd")}
            className="w-[135px] h-[135px] rounded-md"
          />

          <div className="flex flex-col items-start gap-4">
            <div className="gap-0 flex-col items-start">
              <h5 className="font-semibold">
                {t("diploma_request.modal.time")}
              </h5>

              <p className="text-3xl font-semibold text-brand-secondary">
                {dayjs(calendar.date).format("HH:mm")}
              </p>
            </div>

            <div className="detail gap-0 flex-col items-start large">
              <h5 className="font-semibold">
                {t("diploma_request.modal.duration")}
              </h5>

              <p className="text-3xl font-semibold text-brand-secondary">
                20&nbsp;{t("diploma_request.modal.minutes")}
              </p>
            </div>
          </div>
        </div>

        <footer>
          {isRequestedAlready ? (
            <p className="text-sm w-full text-center text-brand">
              {t("diploma_request.modal.reschedule_message")}
            </p>
          ) : (
            <div className="w-full justify-center flex flex-row gap-6">
              <Button
                size="md"
                variant="contained"
                onClick={() => closeModal()}
              >
                {t("btns.back")}
              </Button>

              <LoadingButton
                size="md"
                color="green"
                variant="contained"
                onClick={() => {
                  if (!date && !hour) return;
                  handleConfirm();
                }}
                loading={isLoading}
                disabled={isLoading}
              >
                {t("btns.confirm")}
              </LoadingButton>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}
